import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Paper,
  TextField,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { transceive } from './actions';
import { stringToHex } from './backupService';
import {
  checkAllPIVSlots,
  getPIVSlotStatistics,
  getRecommendedAction,
  PIV_SLOTS
} from './pivSlotDetection';
import PIVSlotStatusCard from './PIVSlotStatusCard';
import OverwriteConfirmationDialog from './OverwriteConfirmationDialog';

// Key algorithms
const KEY_ALGORITHMS = [
  { value: '07', label: 'RSA-2048', description: 'Most compatible, good security' },
  { value: '05', label: 'RSA-1024', description: 'Legacy, not recommended' },
  { value: '06', label: 'RSA-3072', description: 'High security (requires firmware support)' },
  { value: '11', label: 'ECC P-256', description: 'Modern, efficient, good security' },
  { value: '14', label: 'ECC P-384', description: 'High security elliptic curve' }
];

const PIVKeySetupWizard = ({ open, onClose, dispatch }) => {
  const [activeStep, setActiveStep] = useState(0);

  // Status detection states
  const [slotStatuses, setSlotStatuses] = useState([]);
  const [slotStats, setSlotStats] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');

  // Configuration states
  const [slotConfigs, setSlotConfigs] = useState({
    '9A': { mode: 'generate', algorithm: '07' },
    '9C': { mode: 'generate', algorithm: '07' },
    '9D': { mode: 'generate', algorithm: '07' },
    '9E': { mode: 'generate', algorithm: '07' }
  });

  // Overwrite confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  const [pin, setPin] = useState('');
  const [managementKey, setManagementKey] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const steps = ['Welcome & Status Check', 'Configure Slots', 'Authentication', 'Generate/Import', 'Complete'];

  // Detect slot status when wizard opens
  useEffect(() => {
    if (open) {
      detectSlotStatus();
    }
  }, [open]);

  const detectSlotStatus = async () => {
    setStatusLoading(true);
    setStatusError('');

    try {
      // Select PIV applet first
      await dispatch(transceive('00A4040009A00000030800001000'));

      // Check all slots
      const statuses = await checkAllPIVSlots((apdu) => dispatch(transceive(apdu)));
      setSlotStatuses(statuses);

      // Calculate statistics
      const stats = getPIVSlotStatistics(statuses);
      setSlotStats(stats);

      // Set intelligent defaults based on status
      const defaultConfigs = {};
      statuses.forEach((status) => {
        defaultConfigs[status.slotId] = {
          mode: getRecommendedAction(status.status),
          algorithm: '07' // RSA-2048 default
        };
      });
      setSlotConfigs(defaultConfigs);

    } catch (err) {
      console.error('Failed to detect slot status:', err);
      setStatusError(err.message || 'Failed to check slot status');

      // Set default empty status on error
      const emptyStatuses = PIV_SLOTS.map(slot => ({
        slotId: slot.id,
        status: 'error',
        error: 'Detection failed',
        hasCert: false,
        hasPrivateKey: false
      }));
      setSlotStatuses(emptyStatuses);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSlotConfigChange = (slotId, field, value) => {
    // Check if this is a mode change that requires overwrite confirmation
    if (field === 'mode' && value !== 'skip') {
      const slotStatus = slotStatuses.find(s => s.slotId === slotId);

      // If slot is not empty and user wants to generate/import, show confirmation
      if (slotStatus && slotStatus.status !== 'empty') {
        setPendingChange({ slotId, field, value });
        setConfirmOpen(true);
        return;
      }
    }

    // Apply change directly
    setSlotConfigs((prev) => ({
      ...prev,
      [slotId]: { ...prev[slotId], [field]: value }
    }));
  };

  const handleOverwriteConfirm = () => {
    if (pendingChange) {
      setSlotConfigs((prev) => ({
        ...prev,
        [pendingChange.slotId]: {
          ...prev[pendingChange.slotId],
          [pendingChange.field]: pendingChange.value
        }
      }));
      setPendingChange(null);
    }
    setConfirmOpen(false);
  };

  const handleOverwriteCancel = () => {
    setPendingChange(null);
    setConfirmOpen(false);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSlotConfigs({
      '9A': { mode: 'generate', algorithm: '07' },
      '9C': { mode: 'generate', algorithm: '07' },
      '9D': { mode: 'generate', algorithm: '07' },
      '9E': { mode: 'generate', algorithm: '07' }
    });
    setPin('');
    setManagementKey('');
    setProcessing(false);
    setProgress(0);
    setResults([]);
    setError('');
  };

  const handleExecute = async () => {
    setProcessing(true);
    setError('');
    setResults([]);
    setProgress(0);

    try {
      // Select PIV applet
      await dispatch(transceive('00A4040009A00000030800001000'));

      // Verify PIN if needed
      if (pin) {
        const pinHex = stringToHex(pin);
        const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
        const verifyRes = await dispatch(transceive(verifyAPDU));

        if (!verifyRes.endsWith('9000')) {
          throw new Error('PIN verification failed. Please check your PIN.');
        }
      }

      const totalSlots = Object.values(slotConfigs).filter(c => c.mode !== 'skip').length;
      let completed = 0;

      // Process each slot
      for (const slot of PIV_SLOTS) {
        const config = slotConfigs[slot.id];

        if (config.mode === 'skip') {
          continue;
        }

        try {
          if (config.mode === 'generate') {
            // Generate key on card
            const genAPDU = `0047000${slot.id}05AC03${config.algorithm}00`;
            const response = await dispatch(transceive(genAPDU));

            if (response.endsWith('9000')) {
              setResults((prev) => [...prev, {
                slot: slot.id,
                name: slot.name,
                success: true,
                message: 'Key generated successfully'
              }]);
            } else {
              throw new Error(`Generation failed with status: ${response.slice(-4)}`);
            }
          }
          // For 'import' mode, we just show instructions in the UI
          // Actual import would require user to provide key material

          completed++;
          setProgress((completed / totalSlots) * 100);
        } catch (err) {
          setResults((prev) => [...prev, {
            slot: slot.id,
            name: slot.name,
            success: false,
            message: err.message
          }]);
        }
      }

      setActiveStep(4); // Move to complete step
    } catch (err) {
      setError(err.message || 'An error occurred during key setup');
    } finally {
      setProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Welcome & Status Check
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Welcome to PIV Key Setup Wizard
            </Typography>
            <Typography variant="body1" paragraph>
              This wizard will help you configure cryptographic keys for your PIV (Personal Identity Verification) card.
            </Typography>

            <Alert severity="info" style={{ marginBottom: 16 }}>
              <AlertTitle>What is PIV?</AlertTitle>
              PIV is a US government standard for secure identity credentials. It provides 4 key slots for different purposes.
            </Alert>

            {/* Status Detection */}
            {statusLoading && (
              <Box style={{ textAlign: 'center', padding: 32 }}>
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: 16 }}>
                  Checking slot status...
                </Typography>
              </Box>
            )}

            {statusError && (
              <Alert severity="warning" style={{ marginBottom: 16 }}>
                <AlertTitle>Status Check Failed</AlertTitle>
                {statusError}. The wizard will continue with default settings.
              </Alert>
            )}

            {!statusLoading && slotStats && (
              <Paper elevation={1} style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Device Status Summary:
                </Typography>
                <Box style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <Chip
                    label={`${slotStats.empty} Empty`}
                    color="default"
                    size="small"
                  />
                  <Chip
                    label={`${slotStats.complete} Configured`}
                    color="primary"
                    size="small"
                  />
                  {slotStats.keyOnly > 0 && (
                    <Chip
                      label={`${slotStats.keyOnly} Key Only`}
                      color="secondary"
                      size="small"
                    />
                  )}
                  {slotStats.errors > 0 && (
                    <Chip
                      label={`${slotStats.errors} Errors`}
                      color="secondary"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>
            )}

            <Paper elevation={1} style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Available Key Slots:</Typography>
              {PIV_SLOTS.map((slot) => (
                <Box key={slot.id} style={{ marginBottom: 8 }}>
                  <Typography variant="body2">
                    <strong>Slot {slot.id} - {slot.name}:</strong> {slot.description}
                  </Typography>
                </Box>
              ))}
            </Paper>

            <Alert severity="warning">
              <AlertTitle>Important: Key Backup Strategy</AlertTitle>
              <Typography variant="body2" paragraph>
                You have two options for key management:
              </Typography>
              <Typography variant="body2" component="div">
                <strong>1. Generate on Card (Recommended):</strong>
                <ul>
                  <li>Keys generated inside the secure element</li>
                  <li>Maximum security - keys never leave the device</li>
                  <li>⚠️ Cannot be backed up or transferred to another device</li>
                </ul>
              </Typography>
              <Typography variant="body2" component="div">
                <strong>2. Import External Keys:</strong>
                <ul>
                  <li>Keys generated on your computer using tools like OpenSSL</li>
                  <li>Can be backed up and restored to replacement device if lost</li>
                  <li>⚠️ Requires secure backup management (encrypted storage)</li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        );

      case 1: // Configure Slots
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Key Slots
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Review the current status of each slot and configure your desired actions.
            </Typography>

            {slotStats && (
              <Alert
                severity={slotStats.empty === slotStats.total ? 'info' : 'warning'}
                style={{ marginBottom: 16 }}
              >
                <AlertTitle>
                  {slotStats.empty === slotStats.total
                    ? 'All Slots Empty - Ready for Configuration'
                    : 'Some Slots Already Configured'}
                </AlertTitle>
                {slotStats.empty === slotStats.total ? (
                  <Typography variant="body2">
                    Your device has no existing keys. You can safely configure all slots.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {slotStats.complete} slot(s) already have keys and certificates.
                    Replacing them will permanently delete the existing keys.
                    Make sure you have backups if needed.
                  </Typography>
                )}
              </Alert>
            )}

            {PIV_SLOTS.map((slot) => {
              const status = slotStatuses.find(s => s.slotId === slot.id) || {
                slotId: slot.id,
                status: 'empty',
                hasCert: false,
                hasPrivateKey: false
              };
              const config = slotConfigs[slot.id] || { mode: 'generate', algorithm: '07' };

              return (
                <PIVSlotStatusCard
                  key={slot.id}
                  slot={slot}
                  status={status}
                  config={config}
                  onConfigChange={handleSlotConfigChange}
                />
              );
            })}
          </Box>
        );

      case 2: // Authentication
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Authentication Required
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter your PIV PIN and Management Key to proceed with key operations.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Default Credentials</AlertTitle>
              <Typography variant="body2">
                <strong>Default PIN:</strong> 123456<br />
                <strong>Default Management Key:</strong> (leave blank for default)
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="PIV PIN"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Default: 123456"
              helperText="6-8 digit PIN"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Management Key (Optional)"
              type="password"
              value={managementKey}
              onChange={(e) => setManagementKey(e.target.value)}
              placeholder="Leave blank for default"
              helperText="24-byte hex key (48 characters)"
            />
          </Box>
        );

      case 3: // Generate/Import
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {processing ? 'Processing...' : 'Ready to Execute'}
            </Typography>

            {!processing && (
              <>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Review your configuration and click "Execute" to begin key setup.
                </Typography>

                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Configuration Summary:</Typography>
                  {PIV_SLOTS.map((slot) => {
                    const config = slotConfigs[slot.id];
                    if (config.mode === 'skip') return null;

                    const alg = KEY_ALGORITHMS.find(a => a.value === config.algorithm);
                    return (
                      <Box key={slot.id} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>Slot {slot.id}:</strong> {config.mode === 'generate' ? 'Generate' : 'Import'} {alg?.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Paper>

                {Object.values(slotConfigs).some(c => c.mode === 'import') && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>External Key Import Instructions</AlertTitle>
                    <Typography variant="body2" paragraph>
                      For slots configured to import external keys, you'll need to generate keys using external tools first.
                    </Typography>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">View Key Generation Commands</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
{`# Generate RSA-2048 key pair
openssl genrsa -out private.pem 2048
openssl rsa -in private.pem -pubout -out public.pem

# Generate ECC P-256 key pair
openssl ecparam -name prime256v1 -genkey -out private-ec.pem
openssl ec -in private-ec.pem -pubout -out public-ec.pem

# Export to DER format for import
openssl rsa -in private.pem -outform DER -out private.der
openssl pkcs8 -topk8 -inform PEM -outform DER -in private.pem -out private-pkcs8.der -nocrypt`}
                        </Typography>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          After generating keys, use the "Import Key" function in the PIV management page to import them.
                        </Alert>
                      </AccordionDetails>
                    </Accordion>
                  </Alert>
                )}
              </>
            )}

            {processing && (
              <Box>
                <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
                <Typography variant="body2" align="center">
                  {Math.round(progress)}% complete
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {results.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Results:</Typography>
                {results.map((result, idx) => (
                  <Alert
                    key={idx}
                    severity={result.success ? 'success' : 'error'}
                    icon={result.success ? <CheckCircleIcon /> : <WarningIcon />}
                    sx={{ mb: 1 }}
                  >
                    <strong>Slot {result.slot} - {result.name}:</strong> {result.message}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );

      case 4: // Complete
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              Setup Complete
            </Typography>

            <Typography variant="body1" paragraph>
              Your PIV key setup has been completed.
            </Typography>

            {results.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Summary:</Typography>
                {results.map((result, idx) => (
                  <Alert
                    key={idx}
                    severity={result.success ? 'success' : 'warning'}
                    sx={{ mb: 1 }}
                  >
                    <strong>Slot {result.slot} - {result.name}:</strong> {result.message}
                  </Alert>
                ))}
              </Box>
            )}

            <Alert severity="info">
              <AlertTitle>Next Steps</AlertTitle>
              <Typography variant="body2">
                • For card-generated keys: Your keys are ready to use immediately<br />
                • For imported keys: Use the PIV management page to import your external keys<br />
                • Remember to backup your external keys securely if you chose the import option<br />
                • You can generate certificates for your keys using the PIV management page
              </Typography>
            </Alert>

            {Object.values(slotConfigs).some(c => c.mode === 'import') && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Important: Backup Your External Keys</AlertTitle>
                <Typography variant="body2">
                  If you chose to import external keys, make sure to store the private key files in a secure, encrypted location (password manager, encrypted USB drive, etc.). Without these backups, you cannot recover your keys if the device is lost.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={processing}
      >
        <DialogTitle>
          PIV Key Setup Wizard
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} style={{ marginBottom: 24, marginTop: 8 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          {activeStep === 0 && (
            <>
              <Button onClick={onClose} disabled={processing || statusLoading}>
                Cancel
              </Button>
              <Button onClick={handleNext} variant="contained" disabled={statusLoading}>
                Next
              </Button>
            </>
          )}

          {activeStep > 0 && activeStep < 3 && (
            <>
              <Button onClick={handleBack} disabled={processing}>
                Back
              </Button>
              <Button onClick={onClose} disabled={processing}>
                Cancel
              </Button>
              <Button onClick={handleNext} variant="contained" disabled={processing}>
                Next
              </Button>
            </>
          )}

          {activeStep === 3 && (
            <>
              <Button onClick={handleBack} disabled={processing}>
                Back
              </Button>
              <Button onClick={onClose} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleExecute}
                variant="contained"
                disabled={processing || !pin}
              >
                Execute
              </Button>
            </>
          )}

          {activeStep === 4 && (
            <>
              <Button onClick={handleReset}>
                Setup Another Configuration
              </Button>
              <Button onClick={onClose} variant="contained">
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      {pendingChange && (
        <OverwriteConfirmationDialog
          open={confirmOpen}
          onClose={handleOverwriteCancel}
          onConfirm={handleOverwriteConfirm}
          slotInfo={{
            ...PIV_SLOTS.find(s => s.id === pendingChange.slotId),
            ...slotStatuses.find(s => s.slotId === pendingChange.slotId)
          }}
          type="piv"
        />
      )}
    </>
  );
};

export default connect()(PIVKeySetupWizard);
